import React, { useEffect, useState } from 'react';
import { Typography } from 'antd';
import classNames from 'classnames';
import { BringgIcon, BringgFontIcons } from '@bringg/bringg-icons';
import { BringgInput } from 'components';
import { useInputWidth } from './helpers';

import './editable-title.scss';

const Text = Typography.Text;

export interface EditableTitleProps {
	value?: string;
	onValueChange?(newValue: string): void;
	onApplyChanges?(newValue: string): void;
	className?: string;
	placeholder?: string;
	maxLength?: number;
	maxEditLength?: number;
	loading?: boolean;
	tooltip?: boolean;
	forceShowEditIcon?: boolean;
}

const ESCAPE = 27;

const EditableTitle = ({
	value,
	onValueChange,
	onApplyChanges,
	className,
	placeholder = 'Untitled',
	maxLength = 32,
	maxEditLength = 60,
	loading,
	tooltip = true,
	forceShowEditIcon = false
}: EditableTitleProps) => {
	const [innerValue, setInnerValue] = useState<string>(value || '');
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [tempValue, setTempValue] = useState<string>(innerValue);
	const { inputWidth, fakeElRef } = useInputWidth(isEditMode, innerValue);

	useEffect(() => {
		if (innerValue !== value) {
			onValueChange?.(innerValue);
		}
	}, [innerValue]);

	useEffect(() => {
		isEditMode && setTempValue(innerValue);
	}, [isEditMode]);

	useEffect(() => {
		setInnerValue(value || '');
	}, [value]);

	const onKeyDown = e => {
		if (e.keyCode === ESCAPE) {
			setInnerValue(tempValue);
			setIsEditMode(false);
		}
	};

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInnerValue(e.target.value);
	};

	const onInputClick = e => {
		e.stopPropagation();
	};

	const applyChanges = e => {
		e.preventDefault();

		setInnerValue(innerValue);
		setTempValue(innerValue);

		setIsEditMode(false);

		if (onApplyChanges) {
			onApplyChanges(innerValue);
		}
	};

	const toggleEdit = e => {
		e.stopPropagation();

		setIsEditMode(!isEditMode);
	};

	return (
		<div className={classNames('editable-title', className, { loading })}>
			{isEditMode ? (
				<>
					<BringgInput
						type="text"
						className="editable-input"
						value={innerValue}
						onChange={onChange}
						onClick={onInputClick}
						onPressEnter={applyChanges}
						onKeyDown={onKeyDown}
						autoFocus
						placeholder={placeholder}
						maxLength={maxEditLength}
						onBlur={applyChanges}
						bordered={false}
						style={{ width: inputWidth }}
					/>
					<span ref={fakeElRef} className="input-sizer title" data-value={innerValue || placeholder}></span>
				</>
			) : (
				<div className={classNames('title', { 'no-value': !innerValue })} onClick={toggleEdit}>
					<Text
						className={classNames('title-inner-text', { 'no-value': !innerValue })}
						style={{ maxWidth: `${maxLength}ch` }}
						ellipsis={
							tooltip && innerValue.length > maxLength
								? { tooltip: { title: innerValue, overlayClassName: 'bringg-tooltip-overlay' } }
								: undefined
						}
					>
						{innerValue || placeholder}
					</Text>
					<BringgIcon
						className={classNames('edit-title-icon', { 'force-show-edit-icon': forceShowEditIcon })}
						iconName={BringgFontIcons.Pencil}
						data-test-id="edit-title-icon"
					/>
					{loading && (
						<BringgIcon
							iconName={BringgFontIcons.Loader}
							spin
							className="title-loader-icon"
							data-test-id="title-loader-icon"
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default EditableTitle;
